import { useState } from "react";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Textarea } from "../components/ui/Textarea";
import { publicPost } from "../lib/api";

interface FormState {
  full_name: string;
  email: string;
  organization_name: string;
  community_role: string;
  city: string;
  neighborhood: string;
  reason: string;
}

const initial: FormState = {
  full_name: "",
  email: "",
  organization_name: "",
  community_role: "",
  city: "",
  neighborhood: "",
  reason: "",
};

export default function RequestAccess() {
  const [form, setForm] = useState<FormState>(initial);
  const [status, setStatus] = useState<"idle" | "submitting" | "ok" | "err">(
    "idle",
  );
  const [error, setError] = useState<string | null>(null);

  const update =
    (k: keyof FormState) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("submitting");
    setError(null);
    try {
      await publicPost("/api/access-requests", form);
      setStatus("ok");
      setForm(initial);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
      setStatus("err");
    }
  };

  if (status === "ok") {
    return (
      <div className="container-page py-20">
        <div className="surface mx-auto max-w-xl p-8 text-center">
          <h1 className="text-2xl font-semibold text-white">
            Thank you — request received.
          </h1>
          <p className="mt-3 text-slate-300">
            A community admin will review your request and follow up by email.
            We do not share access requests outside the review team.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-page py-16">
      <div className="mx-auto max-w-2xl">
        <h1 className="text-3xl font-semibold text-white">Request access</h1>
        <p className="mt-3 text-slate-300">
          Access is by invitation, granted by community trust. Tell us a little
          about who you are and why you'd like to be part of Community Shield
          OS.
        </p>

        <form className="surface mt-8 grid gap-5 p-6" onSubmit={submit}>
          <div className="grid gap-5 sm:grid-cols-2">
            <Input
              required
              name="full_name"
              label="Full name"
              value={form.full_name}
              onChange={update("full_name")}
            />
            <Input
              required
              type="email"
              name="email"
              label="Email"
              value={form.email}
              onChange={update("email")}
            />
          </div>
          <Input
            name="organization_name"
            label="Organization (optional)"
            value={form.organization_name}
            onChange={update("organization_name")}
            hint="Church, school, business, or community group you're part of."
          />
          <Input
            name="community_role"
            label="Role in community"
            value={form.community_role}
            onChange={update("community_role")}
            placeholder="e.g. business owner, parent, deacon, organizer"
          />
          <div className="grid gap-5 sm:grid-cols-2">
            <Input
              name="city"
              label="City"
              value={form.city}
              onChange={update("city")}
            />
            <Input
              name="neighborhood"
              label="Neighborhood"
              value={form.neighborhood}
              onChange={update("neighborhood")}
            />
          </div>
          <Textarea
            name="reason"
            label="Why are you requesting access?"
            value={form.reason}
            onChange={update("reason")}
            rows={5}
            hint="Briefly tell us how you'd use the platform and any references in the community."
          />
          {error && (
            <p className="rounded-md border border-red-400/30 bg-red-500/10 px-3 py-2 text-sm text-red-200">
              {error}
            </p>
          )}
          <div className="flex justify-end">
            <Button type="submit" disabled={status === "submitting"}>
              {status === "submitting" ? "Submitting…" : "Submit request"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
